import React, { useEffect, useContext } from 'react';
import {
  Box,
  Stack,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import ManualPicker from 'components/_pickers/ManualPicker';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { AppContext } from 'views/App/context';

interface DTBFormFieldsProps {
  orgId: string;
  title: string;
  jurisdictionId: string;
  manualId: string;
  verticalId: string;
  level: 'JURISDICTION' | 'GLOBAL' | 'AGENCY';
  type?: 'CONTENT' | 'VIDEO';
  isNew?: boolean;
  onTitleChange: (val: string) => void;
  onJurisdictionChange: (val: string) => void;
  onManualChange: (val: string) => void;
  onVerticalChange: (val: string) => void;
  onLevelChange: (val: 'JURISDICTION' | 'GLOBAL' | 'AGENCY') => void;
  onTypeChange?: (val: 'CONTENT' | 'VIDEO') => void;
}

const DTBFormFields: React.FC<DTBFormFieldsProps> = ({
  orgId,
  title,
  jurisdictionId,
  manualId,
  isNew = false,
  verticalId,
  level,
  type = 'CONTENT',
  onTitleChange,
  onJurisdictionChange,
  onManualChange,
  onVerticalChange,
  onLevelChange,
  onTypeChange,
}) => {
  const { isLexipolStaff } = useContext(AppContext);

  // Automatically set level based on jurisdictionId only if not Lexipol staff
  useEffect(() => {
    if (!isLexipolStaff) {
      onLevelChange('AGENCY');
    }
  }, [jurisdictionId, onLevelChange, isLexipolStaff]);

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Stack spacing={4}>
        {isNew && (
          <Typography
            variant='h5'
            sx={{
              color: neutral[600],
            }}
          >
            {t('dtbs.library.create.form.new')}
          </Typography>
        )}
        <TextField
          label={t('dtbs.library.create.form.title')}
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          fullWidth
        />
        {isNew && isLexipolStaff && (
          <FormControl sx={{ mt: 0, pt: 0 }}>
            <FormLabel>{t('dtbs.library.create.form.type')}</FormLabel>
            <RadioGroup
              row
              value={type}
              onChange={(e) => onTypeChange?.(e.target.value as 'CONTENT' | 'VIDEO')}
            >
              <FormControlLabel
                value='CONTENT'
                control={<Radio />}
                label={t('dtbs.library.create.form.typeContent')}
              />
              <FormControlLabel
                value='VIDEO'
                control={<Radio />}
                label={t('dtbs.library.create.form.typeVideo')}
              />
            </RadioGroup>
          </FormControl>
        )}
        {isLexipolStaff && (
          <>
            <FormControl sx={{ mt: 0, pt: 0 }}>
              <FormLabel>{t('dtbs.library.level.label')}</FormLabel>
              <RadioGroup
                row
                value={level}
                onChange={(e) => onLevelChange(e.target.value as 'JURISDICTION' | 'GLOBAL')}
              >
                <FormControlLabel
                  value='GLOBAL'
                  control={<Radio />}
                  label={t('dtbs.library.level.GLOBAL')}
                />
                <FormControlLabel
                  value='JURISDICTION'
                  control={<Radio />}
                  label={t('dtbs.library.level.JURISDICTION')}
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
        {level === 'JURISDICTION' && (
          <JurisdictionPicker
            id='jurisdiction-picker'
            label={t('dtbs.library.create.form.jurisdiction')}
            value={jurisdictionId ? [jurisdictionId] : []}
            onChange={(val: string[]) => onJurisdictionChange(val[0] || '')}
            placeholder={t('dtbs.library.create.form.selectJurisdiction')}
          />
        )}
        <Box>
          <ManualPicker
            id='manual-picker'
            label={t('dtbs.library.create.form.manual')}
            organizationId={orgId}
            jurisdictionId={jurisdictionId}
            globalOnly={level === 'GLOBAL'}
            value={manualId ? [manualId] : []}
            onChange={(val: string[]) => onManualChange(val[0] || '')}
            placeholder={t('dtbs.library.create.form.selectManual')}
            disabled={level === 'JURISDICTION' && !jurisdictionId} // Only disabled for jurisdiction level when no jurisdiction is picked
          />
          <FormHelperText>{t('dtbs.library.create.form.manualHelper')}</FormHelperText>
        </Box>
      </Stack>
    </Box>
  );
};

export default DTBFormFields;
